import { imageProps } from '../../imageAssets';
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ThemeToggleNav from '../Logics/ThemeToggleNav';
import { useAuth } from '../../Context/AuthContext';
import Styles from './style.module.css';
const NotificationBell = lazy(() => import('./NotificationBell'));

export default function Header() {
    const { logout, isAuthenticated, isAdmin, isParentSession, isStudentSession } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useSelector((state) => state.theme.mode);

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Switch logo based on theme
    const logo = theme === 'dark' ? 'logoWhite' : 'logoBlack';

    // Scroll detection
    const handleScroll = useCallback(() => {
        setIsScrolled(window.scrollY > 30);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMobileMenuOpen]);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/');
        setIsMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(prev => !prev);
    };

    // Navigation links
    const navLinks = [
        { label: 'الرئيسية', path: '/' },
        { label: 'أولى ثانوي', path: '/FirstSecondary' },
        { label: 'تانية ثانوي', path: '/SecondSecondary' },
        { label: 'تالتة ثانوي', path: '/ThirdSecondary' },
        { label: 'الإحصاء', path: '/Statistics' },
    ];

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    return (
        <>
            <header className={`${Styles.header} ${isScrolled ? Styles.scrolled : ''}`}>
                <div className={Styles.gradientLine} aria-hidden="true" />
                <div className={Styles.navInner}>
                    {/* Logo */}
                    <Link to="/" className={Styles.logoLink}>
                        <img {...imageProps(logo)} alt="Logo" className={Styles.logoImg}  />
                    </Link>

                    {/* Desktop Nav Links */}
                    <nav>
                        <ul className={`${Styles.desktopNav}`}>
                            {navLinks.map((link) => (
                                <li key={link.path} className={Styles.navItem}>
                                    <Link
                                        to={link.path}
                                        className={`${Styles.navLink} ${isActive(link.path) ? Styles.navLinkActive : ''}`}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}

                            {/* Conditional Auth Links (Desktop) */}
                            {isAdmin && (
                                <li className={Styles.navItem}>
                                    <Link
                                        to="/Dashboard"
                                        className={`${Styles.navLink} ${isActive('/Dashboard') ? Styles.navLinkActive : ''}`}
                                    >
                                        Dashboard
                                    </Link>
                                </li>
                            )}
                            {isStudentSession && !isAdmin && (
                                <li className={Styles.navItem}>
                                    <Link
                                        to="/profile"
                                        className={`${Styles.navLink} ${isActive('/profile') ? Styles.navLinkActive : ''}`}
                                    >
                                        حسابي
                                    </Link>
                                </li>
                            )}
                            {isParentSession && !isAdmin && (
                                <li className={Styles.navItem}>
                                    <Link
                                        to="/parent"
                                        className={`${Styles.navLink} ${isActive('/parent') ? Styles.navLinkActive : ''}`}
                                    >
                                        متابعة ولي الأمر
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </nav>

                    {/* Right Actions */}
                    <div className={Styles.actionsGroup}>
                        {isAuthenticated && <Suspense fallback={null}><NotificationBell /></Suspense>}
                        <ThemeToggleNav />

                        {/* Desktop CTA Buttons */}
                        <div className={`${Styles.desktopActions}`}>
                            {isAuthenticated ? (
                                <button className={`${Styles.ctaBtn} ${Styles.ctaLogout}`} onClick={handleLogout}>
                                    تسجيل الخروج
                                </button>
                            ) : (
                                <>
                                    <button className={`${Styles.ctaBtn} ${Styles.ctaPrimary}`} onClick={() => navigate('/login')}>
                                        تسجيل الدخول
                                    </button>
                                    <button className={`${Styles.ctaBtn} ${Styles.ctaOutline}`} onClick={() => navigate('/register')}>
                                        إنشاء حساب
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Hamburger (Mobile) */}
                        <button
                            className={`${Styles.hamburger} ${isMobileMenuOpen ? Styles.hamburgerOpen : ''}`}
                            onClick={toggleMobileMenu}
                            aria-label="Toggle menu"
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="mobile-navigation"
                        >
                            <span className={Styles.hamburgerLine}></span>
                            <span className={Styles.hamburgerLine}></span>
                            <span className={Styles.hamburgerLine}></span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Overlay Menu */}
            <div id="mobile-navigation" className={`${Styles.mobileOverlay} ${isMobileMenuOpen ? Styles.mobileOverlayOpen : ''}`}>
                <ul className={Styles.mobileNav}>
                    {navLinks.map((link) => (
                        <li key={link.path} className={Styles.mobileNavItem}>
                            <Link
                                to={link.path}
                                className={`${Styles.mobileLink} ${isActive(link.path) ? Styles.mobileLinkActive : ''}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}

                    {/* Conditional Auth Links (Mobile) */}
                    {isAdmin && (
                        <li className={Styles.mobileNavItem}>
                            <Link
                                to="/Dashboard"
                                className={`${Styles.mobileLink} ${isActive('/Dashboard') ? Styles.mobileLinkActive : ''}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Dashboard
                            </Link>
                        </li>
                    )}
                    {isStudentSession && !isAdmin && (
                        <li className={Styles.mobileNavItem}>
                            <Link
                                to="/profile"
                                className={`${Styles.mobileLink} ${isActive('/profile') ? Styles.mobileLinkActive : ''}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                حسابي
                            </Link>
                        </li>
                    )}
                    {isParentSession && !isAdmin && (
                        <li className={Styles.mobileNavItem}>
                            <Link
                                to="/parent"
                                className={`${Styles.mobileLink} ${isActive('/parent') ? Styles.mobileLinkActive : ''}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                متابعة ولي الأمر
                            </Link>
                        </li>
                    )}

                    {/* Mobile CTA */}
                    <li className={`${Styles.mobileNavItem} ${Styles.mobileCta}`}>
                        {isAuthenticated ? (
                            <button className={`${Styles.ctaBtn} ${Styles.ctaLogout}`} onClick={handleLogout}>
                                تسجيل الخروج
                            </button>
                        ) : (
                            <>
                                <button className={`${Styles.ctaBtn} ${Styles.ctaPrimary}`} onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}>
                                    تسجيل الدخول
                                </button>
                                <button className={`${Styles.ctaBtn} ${Styles.ctaOutline}`} onClick={() => { navigate('/register'); setIsMobileMenuOpen(false); }}>
                                    إنشاء حساب
                                </button>
                            </>
                        )}
                    </li>
                </ul>
            </div>
        </>
    );
}
